<template>
  <span v-if="asyncGoal.ready">
    <ion-item-sliding ref="sliding">
      <ion-item button detail="false" @click="$router.push(`/item/${goal.key}`)">
        <ion-label>
          <ion-text>{{ goal.title }}</ion-text>
          <br>
          <ion-note v-tl:ITEM_DUE_BY="goal.endDate" />
        </ion-label>
        <ion-badge :color="goalToPaceColor(goal)">
          {{ goal.unitsComplete }} / {{ goal.unitsDue }}
        </ion-badge>
      </ion-item>
      <template v-if="!goal.archived">
        <ion-item-options side="end">
          <ion-item-option color="secondary" @click="archiveItem(goal)">
            <ion-icon :icon="archive" />
            <span v-tl:ITEM_ACTION_ARCHIVE />
          </ion-item-option>
        </ion-item-options>
        <ion-item-options side="start">
          <ion-item-option color="tertiary" @click="editItem(goal)">
            <ion-icon :icon="create" />
            <span v-tl:ITEM_ACTION_EDIT />
          </ion-item-option>
        </ion-item-options>
      </template>
      <template v-else>
        <ion-item-options side="start">
          <ion-item-option color="tertiary" @click="restoreItem(goal)">
            <ion-icon :icon="refresh" />
            <span v-tl:ITEM_ACTION_RESTORE />
          </ion-item-option>
        </ion-item-options>
        <ion-item-options side="end">
          <ion-item-option color="danger" @click="deleteItem(goal)">
            <ion-icon :icon="trash" />
            <span v-tl:ITEM_ACTION_DELETE />
          </ion-item-option>
        </ion-item-options>
      </template>
    </ion-item-sliding>
  </span>
  <span v-else>
    <ion-item>
      <ion-label>
        <h2>
          <ion-skeleton-text :animated="true" style="width: 15em" />
        </h2>
        <ion-note>
          <ion-skeleton-text :animated="true" style="width: 8em" />
        </ion-note>
      </ion-label>
      <ion-skeleton-text slot="end" style="width: 3em; height: 1em" />
    </ion-item>
  </span>
</template>
<script>
import { trash, create, refresh, archive } from 'ionicons/icons';
import { differenceInCalendarDays } from 'date-fns';
import { IonText, IonItemOptions, IonItemOption, IonItemSliding, IonIcon,IonLabel,IonNote, IonItem, IonBadge, IonSkeletonText, alertController } from '@ionic/vue';
import { defineComponent } from 'vue';
import { calculatePace } from '@/goal';
import { ymdToDate } from '@/util';
import { asyncObject } from '@/async';

export default defineComponent({
    name: 'ListGoal',
    components: {
        IonIcon,
        IonText,
        IonNote,
        IonBadge,
        IonItem,
        IonLabel,
        IonItemOptions,
        IonItemOption,
        IonItemSliding,
        IonSkeletonText,
    },
    props: {
        goalKey: {
            required: true,
            type: String,
        },
    },

    setup() {
        return {
            trash,
            archive,
            create,
            refresh,
        };
    },

    data() {
        const { $store, goalKey } = this;
        return {
            asyncGoal: asyncObject.wrap($store.dispatch('goals/loadGoal', goalKey)),
        };
    },

    computed: {
        goal() {
            return this.asyncGoal.value;
        },
    },
	
    methods: {
        async archiveItem(goal) {
            this.closeSliding();
            await this.$store.dispatch('goals/archive', goal);
        },

        async deleteItem(goal) {
            this.closeSliding();

            const {$L} = this;
            const alert = await alertController.create({
                header: $L('CONFIRM'),
                message: $L('HOME_CONFIRM_DELETE_GOAL'),
                buttons: [
                    { text: $L('CANCEL'), role: 'cancel' },
                    { text: $L('OK'), role: 'ok' },
                ],
            });

            await alert.present();
            const { role } = await alert.onDidDismiss();
            if (role === 'ok')
                await this.$store.dispatch('goals/delete', goal);
        },

        async restoreItem(goal) {
            this.closeSliding();
            await this.$store.dispatch('goals/restore', goal);
        },

        editItem(goal) {
            this.$router.push(`/edit/${goal.key}`);
            this.closeSliding();
        },
    
        goalToPaceColor(goal) {
            const startDate = ymdToDate(goal.startDate);
            const endDate = ymdToDate(goal.endDate);
            const remainingWork = goal.unitsDue - goal.unitsComplete;
            const remainingTerm = differenceInCalendarDays(endDate, this.$today, false);
            if (remainingTerm < 0 && remainingWork > 0) return 'danger';
            
            const totalTerm = differenceInCalendarDays(endDate, startDate);
            const {unitsPerDay: bl } = calculatePace(goal.unitsDue, totalTerm);
            const { unitsPerDay: adj } = calculatePace(remainingWork, remainingTerm, true);
            
            if (remainingWork <= 0 || adj <= bl) return 'success';
            return 'warning';
        },

        closeSliding() {
            this.$refs.sliding.$el.close();
        },
    },
});

</script>